import { useEffect } from 'react';
import { captureAppError } from '@leagueplatform/observability';
import {
  getAbsoluteRootUrl,
  handleFetchResult,
  openDecodedLink,
  openLoadingTab,
  resolveAbsoluteUrl,
  shouldIntercept,
  fetchDataWithAppropriateToken,
} from 'utils/helpers';

interface InterceptPattern {
  path: string;
  contentType: 'text/html' | 'application/pdf';
  useRootUrl?: boolean;
  partnerId?: string;
}

interface LinkInterceptorConfig {
  patternsToIntercept: InterceptPattern[];
  rootUrl: string;
}

export function useInterceptLinkClick({
  patternsToIntercept,
  rootUrl,
}: LinkInterceptorConfig): void {
  useEffect(() => {
    const handleClick = async (event: MouseEvent): Promise<void> => {
      let element = event.target as HTMLElement | null;

      while (element && element.tagName !== 'A' && element.parentElement) {
        element = element.parentElement;
      }

      if (!element || element.tagName !== 'A') {
        return;
      }

      const href = element.getAttribute('href');
      if (!href) {
        return;
      }

      // Handle tel: links
      if (href.startsWith('tel:')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Handle benefits phone number links (e.g., /benefits/855-417-7102)
      if (
        href.includes('/benefits/') &&
        /\/benefits\/\d{3}-\d{3}-\d{4}/.test(href)
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      try {
        const absoluteHref = resolveAbsoluteUrl(href);
        const path = new URL(absoluteHref).pathname;

        const matchedPattern = patternsToIntercept.find(
          ({ path: pattern, contentType }) => {
            const willIntercept =
              contentType === 'text/html'
                ? shouldIntercept(element as HTMLAnchorElement, pattern)
                : path
                    .split('/')
                    .filter(Boolean)
                    .includes(pattern.replace(/^\/|\/$/g, ''));
            return willIntercept;
          },
        );

        if (matchedPattern) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const targetUrl = matchedPattern.useRootUrl
            ? new URL(href, getAbsoluteRootUrl(rootUrl)).href
            : absoluteHref;

          const tab = openLoadingTab();

          const result = await fetchDataWithAppropriateToken(
            targetUrl,
            matchedPattern.contentType,
            matchedPattern.partnerId,
          );

          handleFetchResult(tab, result, matchedPattern.contentType);
          return;
        }

        if (absoluteHref.includes('open-link?')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          openDecodedLink(absoluteHref);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        captureAppError(error instanceof Error ? error : new Error(message));
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    document.addEventListener('auxclick', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      document.removeEventListener('auxclick', handleClick, { capture: true });
    };
  }, [patternsToIntercept, rootUrl]);
}
